import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PlayCircle, Settings, History, ChevronRight, Skull, Moon, Sun,
  ArrowLeft, ArrowRight, Users, ScrollText, Timer, Eye, Flame, Trophy, LogIn, User, Shield,
} from 'lucide-react';
import { listGames, listRoles } from '@/api/mafia';
import { useTranslation } from '@/i18n/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const RED = '#DC2626';
const RED_DEEP = '#7F1D1D';

export default function MafiaHubPage() {
  const navigate = useNavigate();
  const { t, tRole, tWinner } = useTranslation();
  const { gamer, host } = useMafiaAuth();
  const [activeGames, setActiveGames] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [gamesData, rolesData] = await Promise.all([listGames(), listRoles()]);
        const games = gamesData.games || [];
        setActiveGames(games.filter((g) => g.status !== 'ended').slice(0, 4));
        setRecentGames(games.filter((g) => g.status === 'ended').slice(0, 6));
        setRoles(rolesData.roles || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const rolesByTeam = (team) => roles.filter((r) => r.team === team);

  const TEAM_META = {
    red: { accent: '#F43F5E', icon: '🛡️' },
    black: { accent: '#52525B', icon: '🌙' },
    gray: { accent: '#A78BFA', icon: '🎯' },
  };

  return (
    <div
      className="min-h-screen text-[#F5EBE7] ms-page"
      data-testid="mafia-hub-page"
      style={{ background: 'var(--ms-bg)' }}
    >
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 border border-[#DC2626] flex items-center justify-center">
              <Skull size={13} style={{ color: RED }} />
            </div>
            <span className="font-serif uppercase tracking-[0.3em] text-sm font-semibold text-[#F5F5F5]">
              Mafia <span className="text-[#A3A3A3] font-normal">/ Salon</span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            <button
              onClick={() => navigate('/mafia/roles')}
              className="px-3 py-2 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors flex items-center gap-1.5"
            >
              <ScrollText size={12} /> {t('hub.roles')}
            </button>
            <button
              onClick={() => navigate('/mafia/history')}
              className="px-3 py-2 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors flex items-center gap-1.5"
            >
              <History size={12} /> {t('hub.history')}
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <span className="hidden md:block h-5 w-px bg-white/10" />
            {host && (
              <button
                onClick={() => navigate('/host')}
                data-testid="hub-host-link"
                className="hidden md:flex text-[11px] uppercase tracking-[0.25em] text-[#DC2626] hover:text-white transition-colors items-center gap-1.5"
              >
                <Shield size={12} /> Host
              </button>
            )}
            {gamer ? (
              <button
                onClick={() => navigate('/profile')}
                data-testid="hub-profile-link"
                className="flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-[#DC2626] transition-colors"
              >
                {gamer.picture
                  ? <img src={gamer.picture} alt="" className="w-5 h-5 object-cover" />
                  : <User size={12} className="text-[#A3A3A3]" />}
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#F5F5F5] hidden sm:inline">{gamer.points} pts</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/mafia/login')}
                data-testid="hub-signin-link"
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] text-[#F5F5F5] bg-[#DC2626] hover:bg-[#B91C1C] transition-colors"
              >
                <LogIn size={12} /> Sign in
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              data-testid="mafia-hub-back"
              className="hidden md:flex text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors items-center gap-1.5"
            >
              {t('menu.vangard')} <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-12 py-14 md:py-20">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-7 text-[10px] uppercase tracking-[0.4em]" style={{ color: RED }}>
                <span className="h-px w-8 bg-current" />
                <span>{t('hub.salon-game')}</span>
              </div>

              <h1 className="font-serif uppercase leading-[0.88] text-[#F5F5F5]">
                <span className="block text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight">
                  Mafia
                </span>
                <span className="block text-xl md:text-2xl lg:text-3xl tracking-[0.5em] mt-3 text-[#A3A3A3] font-medium">
                  S A L O N
                </span>
              </h1>

              <p className="mt-8 max-w-2xl text-[#A3A3A3] text-base leading-relaxed">
                {t('hub.hero-desc')}
              </p>

              <div className="mt-8 flex flex-wrap gap-2">
                {host ? (
                  <button
                    onClick={() => navigate('/mafia/new')}
                    data-testid="mafia-hero-new-game"
                    className="px-7 py-3.5 text-[11px] tracking-[0.3em] uppercase text-white font-bold bg-[#DC2626] hover:bg-[#B91C1C] transition-colors flex items-center gap-3"
                  >
                    <PlayCircle size={14} /> {t('hub.start-new')}
                  </button>
                ) : !gamer ? (
                  <button
                    onClick={() => navigate('/mafia/login')}
                    data-testid="mafia-hero-signin"
                    className="px-7 py-3.5 text-[11px] tracking-[0.3em] uppercase text-white font-bold bg-[#DC2626] hover:bg-[#B91C1C] transition-colors flex items-center gap-3"
                  >
                    <LogIn size={14} /> Sign in to play
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/profile')}
                    data-testid="mafia-hero-profile"
                    className="px-7 py-3.5 text-[11px] tracking-[0.3em] uppercase text-white font-bold bg-[#DC2626] hover:bg-[#B91C1C] transition-colors flex items-center gap-3"
                  >
                    <User size={14} /> My profile
                  </button>
                )}
                <button
                  onClick={() => navigate('/mafia/history')}
                  data-testid="mafia-hero-history"
                  className="px-5 py-3.5 text-[11px] tracking-[0.3em] uppercase text-[#F5F5F5] border border-white/20 hover:border-[#F5F5F5] transition-colors flex items-center gap-2"
                >
                  <History size={13} /> {t('hub.history')}
                </button>
              </div>
            </motion.div>
          </div>

          {/* Right column — vertical data strip */}
          <div className="lg:col-span-4 flex flex-col justify-end">
            <div className="border border-white/10 bg-[#0A0303]">
              <div className="border-b border-white/8 px-5 py-3">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">
                  {t('hub.salon-game')}
                </p>
              </div>
              {[
                { v: roles.length || 19, l: t('hub.stat-roles') },
                { v: '10–25', l: t('hub.stat-players') },
                { v: 3, l: t('hub.stat-teams') },
              ].map((s, i, arr) => (
                <div
                  key={s.l}
                  className={`flex items-baseline justify-between px-5 py-4 ${i < arr.length - 1 ? 'border-b border-white/5' : ''}`}
                >
                  <span className="text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3]">{s.l}</span>
                  <span className="font-serif text-3xl font-bold text-[#F5F5F5] tabular-nums">{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVE TABLES */}
      {activeGames.length > 0 && (
        <section className="border-b border-white/8">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
            <SectionHeader
              label={t('hub.in-progress')}
              title={t('hub.active-tables')}
              action={
                <button
                  onClick={() => navigate('/mafia/history')}
                  className="text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors"
                >
                  {t('common.view-all')}
                </button>
              }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {activeGames.map((g) => (
                <button
                  key={g.id}
                  onClick={() => navigate(`/mafia/game/${g.id}`)}
                  data-testid={`mafia-active-game-${g.id}`}
                  className="flex items-center justify-between p-4 border border-white/10 bg-[#0A0303] hover:border-[#DC2626] hover:bg-[#150505] transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className="w-10 h-10 flex items-center justify-center border shrink-0"
                      style={{
                        background: g.current_phase === 'day' ? '#1A1308' : '#1A0707',
                        borderColor: g.current_phase === 'day' ? '#92531A' : '#7F1D1D',
                      }}
                    >
                      {g.current_phase === 'day' ? <Sun size={15} className="text-amber-400" /> : <Moon size={15} style={{ color: RED }} />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#F5F5F5] text-sm font-medium truncate">{g.name}</p>
                      <p className="text-[11px] text-[#A3A3A3] mt-0.5 uppercase tracking-wider">
                        {g.player_count} · {g.status === 'setup' ? t('common.setup') : `${t('common.round')} ${g.current_round}`}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: RED }} />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENT GAMES */}
      {recentGames.length > 0 && (
        <section className="border-b border-white/8">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
            <SectionHeader
              label={t('hub.recent')}
              title={t('history.finished')}
              action={
                <button
                  onClick={() => navigate('/mafia/history')}
                  data-testid="hub-history-view-all"
                  className="text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors"
                >
                  {t('common.view-all')}
                </button>
              }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {recentGames.map((g) => {
                const teamAccent =
                  g.winners === 'red' ? '#F43F5E'
                  : g.winners === 'black' ? '#A1A1AA'
                  : '#A78BFA';
                const teamText =
                  g.winners === 'red' ? 'text-rose-300'
                  : g.winners === 'black' ? 'text-zinc-200'
                  : 'text-violet-300';
                return (
                  <button
                    key={g.id}
                    onClick={() => navigate(`/mafia/game/${g.id}`)}
                    data-testid={`hub-recent-game-${g.id}`}
                    className="flex items-stretch border border-white/10 bg-[#0A0303] hover:border-[#DC2626] hover:bg-[#150505] transition-colors text-left"
                  >
                    <div
                      className="w-1 shrink-0"
                      style={{ background: teamAccent }}
                    />
                    <div className="flex-1 min-w-0 p-4">
                      <p className="text-[#F5F5F5] text-sm font-medium truncate">{g.name}</p>
                      <p className={`text-[10px] uppercase tracking-[0.25em] mt-1 ${teamText}`}>
                        {tWinner(g.winners)}
                      </p>
                      <p className="text-[10px] text-[#A3A3A3] mt-3 uppercase tracking-wider flex items-center gap-2">
                        <Users size={9} /> {g.player_count}
                        {g.ended_at && (
                          <>
                            <span className="opacity-40">·</span>
                            <span>{(g.ended_at || '').slice(0, 10)}</span>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center pr-4">
                      <ChevronRight size={15} className="text-[#A3A3A3]" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* THREE TEAMS */}
      <section className="border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <SectionHeader label={t('hub.three-powers')} title={t('hub.three-teams')} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {['red', 'black', 'gray'].map((team) => {
              const meta = TEAM_META[team];
              const teamRoles = rolesByTeam(team);
              return (
                <div
                  key={team}
                  className="border border-white/10 bg-[#0A0303] flex flex-col"
                >
                  <div className="flex items-center justify-between border-b border-white/8 px-5 py-3">
                    <span className="text-2xl leading-none">{meta.icon}</span>
                    <span
                      className="text-[10px] uppercase tracking-[0.3em] font-semibold"
                      style={{ color: meta.accent }}
                    >
                      {t(`team.${team}.sub`)}
                    </span>
                  </div>
                  <div className="px-5 py-5 flex-1 flex flex-col">
                    <h3 className="font-serif text-2xl uppercase text-[#F5F5F5] font-bold tracking-wide">
                      {t(`team.${team}.name`)}
                    </h3>
                    <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed flex-1">{t(`team.${team}.goal`)}</p>
                    <div className="mt-5 pt-4 border-t border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">
                          {t('team.roles')}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#F5F5F5] tabular-nums">
                          {teamRoles.length}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {teamRoles.slice(0, 8).map((r) => (
                          <span
                            key={r.id}
                            className="inline-flex items-center gap-1 px-1.5 py-1 text-[11px] bg-black border border-white/10 text-[#F5F5F5]/85"
                            title={r.description}
                          >
                            <span>{r.icon}</span>{tRole(r.name)}
                          </span>
                        ))}
                        {teamRoles.length > 8 && (
                          <span className="text-[11px] text-[#A3A3A3] self-center px-1.5">+{teamRoles.length - 8}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <SectionHeader label={t('hub.workflow')} title={t('hub.how-host')} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              { n: '01', icon: Users, title: t('wf.step1.title'), body: t('wf.step1.body') },
              { n: '02', icon: Moon, title: t('wf.step2.title'), body: t('wf.step2.body') },
              { n: '03', icon: Flame, title: t('wf.step3.title'), body: t('wf.step3.body') },
            ].map((s) => (
              <div key={s.n} className="border border-white/10 bg-[#0A0303]">
                <div className="flex items-center justify-between border-b border-white/8 px-5 py-3">
                  <s.icon size={16} style={{ color: RED }} strokeWidth={1.8} />
                  <span className="font-serif text-xs uppercase tracking-[0.4em] font-bold text-[#A3A3A3]">
                    {s.n}
                  </span>
                </div>
                <div className="px-5 py-5">
                  <h3 className="font-serif text-lg uppercase text-[#F5F5F5] font-bold tracking-wide">{s.title}</h3>
                  <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS / TILES — host only */}
      {host && (
        <section className="border-b border-white/8">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
            <SectionHeader label="Host actions" title="Run a table" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <ActionTile
                testid="mafia-tile-new-game"
                icon={PlayCircle}
                title={t('tile.new-game')}
                subtitle={t('tile.new-game-sub')}
                onClick={() => navigate('/mafia/new')}
                accent
              />
              <ActionTile
                testid="mafia-tile-role-library"
                icon={ScrollText}
                title={t('tile.roles-lib')}
                subtitle={t('tile.roles-lib-sub')}
                onClick={() => navigate('/mafia/roles')}
              />
              <ActionTile
                testid="mafia-tile-game-history"
                icon={History}
                title={t('tile.history')}
                subtitle={t('tile.history-sub')}
                onClick={() => navigate('/mafia/history')}
              />
            </div>
          </div>
        </section>
      )}

      {/* RECENT GAMES — moved into the top area, below hero/active tables */}

      {/* FOOTER CTA */}
      <section className="bg-[#080202]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: RED }}>
              {host ? t('hub.cta-begin') : gamer ? 'Your profile' : 'Join the salon'}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl uppercase text-[#F5F5F5] font-bold leading-tight">
              {host ? t('hub.cta-title')
                : gamer ? 'Track points, climb the rank'
                : 'Sign in. Get a member card. Earn points.'}
            </h2>
            <p className="text-[#A3A3A3] mt-4 text-base max-w-xl">
              {host ? t('hub.cta-sub')
                : gamer ? 'Your member card, QR and live stats are one tap away.'
                : 'A Google account is all you need. Your seat at the table is waiting.'}
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            {host ? (
              <button
                onClick={() => navigate('/mafia/new')}
                data-testid="mafia-footer-cta"
                className="px-8 py-4 text-[11px] tracking-[0.3em] uppercase text-white font-bold bg-[#DC2626] hover:bg-[#B91C1C] transition-colors inline-flex items-center gap-3"
              >
                <Timer size={14} /> {t('hub.cta-begin')}
              </button>
            ) : gamer ? (
              <button
                onClick={() => navigate('/profile')}
                data-testid="mafia-footer-cta"
                className="px-8 py-4 text-[11px] tracking-[0.3em] uppercase text-white font-bold bg-[#DC2626] hover:bg-[#B91C1C] transition-colors inline-flex items-center gap-3"
              >
                <User size={14} /> Open profile
              </button>
            ) : (
              <button
                onClick={() => navigate('/mafia/login')}
                data-testid="mafia-footer-cta"
                className="px-8 py-4 text-[11px] tracking-[0.3em] uppercase text-white font-bold bg-[#DC2626] hover:bg-[#B91C1C] transition-colors inline-flex items-center gap-3"
              >
                <LogIn size={14} /> Sign in
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ label, title, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-white/8">
      <div>
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#DC2626] mb-2">{label}</p>
        <h2 className="font-serif text-2xl md:text-3xl uppercase text-[#F5F5F5] font-bold tracking-wide">{title}</h2>
      </div>
      {action}
    </div>
  );
}

function ActionTile({ icon: Icon, title, subtitle, onClick, testid, accent }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group flex flex-col p-5 text-left border transition-colors ${
        accent
          ? 'bg-[#DC2626] border-[#DC2626] hover:bg-[#B91C1C]'
          : 'bg-[#0A0303] border-white/10 hover:border-[#DC2626] hover:bg-[#150505]'
      }`}
    >
      <div className="flex items-center justify-between">
        <Icon size={20} strokeWidth={1.8} className={accent ? 'text-white' : 'text-[#DC2626]'} />
        <ChevronRight size={16} className={accent ? 'text-white/80' : 'text-[#A3A3A3]'} />
      </div>
      <h3 className={`font-serif text-xl uppercase font-bold tracking-wide mt-8 ${accent ? 'text-white' : 'text-[#F5F5F5]'}`}>
        {title}
      </h3>
      <p className={`text-sm mt-1 ${accent ? 'text-white/80' : 'text-[#A3A3A3]'}`}>{subtitle}</p>
    </button>
  );
}
